import { useState } from 'react';

// mui
import { Box, Skeleton } from '@mui/material';

// assets
import { TransparentImage } from '../asset/image.js';

// client-shared
import { path } from '@goldblack/client-utils';

import { def } from '@goldblack/shared';

// Image with a skeleton placeholder shown until it loads, then fades in.
// Fixed-ratio media (explore/profile) sizes the skeleton from the image's own aspect box.
// 'auto' media (feed/dialog) reserves the upload's real aspect (post width/height, portrait
// 3:4 when unknown) until loaded. A failed load (e.g. still PROCESSING, media not uploaded
// yet) keeps that box instead of collapsing to zero height.
// A null `url` renders only the placeholder box and never requests the media.
function MediaImage({ id, url, aspectRatio, blurSx, size }) {
    const [state, setState] = useState(url ? 'loading' : 'failed'); // 'loading' | 'loaded' | 'failed'
    const loaded = state === 'loaded';
    const placeholderAspect = size?.width > 0 && size?.height > 0 ? size.width / size.height : 3 / 4;
    // Fixed-ratio tiles get their height from the <img>'s own aspectRatio; with no <img> (url null)
    // or before it loads, the box must carry the ratio itself or it collapses to zero height.
    const reservedAspect = loaded ? undefined : aspectRatio === 'auto' ? placeholderAspect : aspectRatio;
    return (
        <Box sx={{ position: 'relative', width: '100%', aspectRatio: reservedAspect }}>
            {/* No media requested (PROCESSING): no skeleton, just the slot border (same as an empty slot, for everyone). */}
            {!url && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        border: 1,
                        borderColor: 'primary.main',
                        pointerEvents: 'none',
                    }}
                />
            )}
            {!loaded && !!url && (
                <Skeleton
                    variant='rectangular'
                    animation={state === 'loading' ? 'wave' : false}
                    sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                />
            )}
            {url && (
                <Box
                    component='img'
                    id={id}
                    src={url}
                    loading='lazy'
                    alt=''
                    onLoad={() => setState('loaded')}
                    onError={() => setState('failed')}
                    sx={{
                        display: 'block',
                        width: '100%',
                        height: '100%',
                        aspectRatio,
                        objectFit: 'cover',
                        opacity: loaded ? 1 : 0,
                        transition: 'opacity 0.2s ease',
                        ...blurSx,
                    }}
                />
            )}
        </Box>
    );
}

function PostShowMedia(props) {
    // iOS 때문에 필요하지만 다른 OS에서도 무해 → 그냥 공통 적용 추천
    const iOSAtrributes = {
        playsInline: true,
        'webkit-playsinline': 'true',
        disablePictureInPicture: true,
        controlsList: 'nofullscreen noremoteplayback',
    };

    const blurSx = props.blurred ? { filter: 'blur(20px)', transform: 'scale(1.05)' } : {};

    const videoComponent = (id, url) => {
        return (
            <Box
                component='video'
                id={id}
                key={url}
                src={url}
                width='100%'
                height='100%'
                autoPlay={props.autoPlay}
                muted={props.muted}
                loop={props.loop}
                onLoadedMetadata={props.onLoadedMetadata}
                onPlay={props.onPlay}
                {...iOSAtrributes}
                sx={{
                    display: 'block',
                    aspectRatio: props.aspectRatio,
                    objectFit: 'cover',
                    ...blurSx,
                }}
            />
        );
    };

    const imageComponent = (id, url) => (
        <MediaImage key={url} id={id} url={url} aspectRatio={props.aspectRatio} blurSx={blurSx} size={props.post} />
    );

    const showMedia = (post) => {
        let thumbnailUrl = post ? post.thumbnail : null;
        let imageUrl = post ? post.image : TransparentImage;
        let id = post ? post._id : '';
        let isVideo = path.getContentType(imageUrl).startsWith('video');
        // While PROCESSING the media may not be in R2 yet; requesting it now gets a 404 that the
        // CDN caches for minutes, which then also fails the ML box's download of the same URL
        // (post stuck PROCESSING). So show only the sized placeholder; local blob: previews are fine.
        if (post?.status === def.PostStatus.PROCESSING && !imageUrl.startsWith('blob:')) {
            return imageComponent(id, null);
        }
        if (isVideo) {
            if (props.showThumbnail && thumbnailUrl) {
                return imageComponent(id, thumbnailUrl);
            } else {
                return videoComponent(id, imageUrl);
            }
        } else {
            return imageComponent(id, imageUrl);
        }
    };

    return showMedia(props.post);
}

function PostShowMediaDialog(post, muted, onPlay, onLoadedMetadata, blurred, showThumbnail) {
    let props = {
        post: post,
        autoPlay: true,
        muted: muted,
        loop: true,
        aspectRatio: 'auto',
        onPlay: onPlay,
        onLoadedMetadata: onLoadedMetadata,
        blurred: blurred,
        showThumbnail: showThumbnail,
    };
    return <PostShowMedia {...props} />;
}

function PostShowMediaExplore(post) {
    let props = { post: post, aspectRatio: 3 / 4, showThumbnail: true };
    return <PostShowMedia {...props} />;
}

function PostShowMediaProfile(post) {
    let props = {
        post: post,
        aspectRatio: 1,
        showThumbnail: true,
        blurred:
            post?.status === def.PostStatus.BLOCKED ||
            post?.status === def.PostStatus.CENSORED ||
            post?.status === def.PostStatus.PROCESSING,
    };
    return <PostShowMedia {...props} />;
}

export { PostShowMediaDialog, PostShowMediaExplore, PostShowMediaProfile };
